<script lang="ts" setup>
import { ref } from "vue";
import { GoogleMap } from "vue3-google-map";
import { useI18n } from "vue-i18n";
import type { GoogleMapCoordinates } from "@/api/dto/coordinates.dto";

type MapTypeId = "roadmap" | "hybrid";

withDefaults(
  defineProps<{
    apiKey: string;
    center?: GoogleMapCoordinates;
    zoom: number;
    mapId?: string;
    disableDefaultUi?: boolean;
    fullscreenControl?: boolean;
    streetViewControl?: boolean;
  }>(),
  {
    mapId: undefined,
    disableDefaultUi: false,
    fullscreenControl: false,
    streetViewControl: false,
    center: undefined,
  },
);

const { t } = useI18n();

const googleMapRef = ref<InstanceType<typeof GoogleMap> | null>(null);
const mapTypeId = ref<MapTypeId>("roadmap");

function toggleMapType(): void {
  mapTypeId.value = mapTypeId.value === "roadmap" ? "hybrid" : "roadmap";
}

defineExpose({ googleMapRef, mapTypeId });
</script>

<template>
  <div class="omni-google-map">
    <google-map
      ref="googleMapRef"
      :api-key="apiKey"
      :center="center"
      :disable-default-ui="disableDefaultUi"
      :fullscreen-control="fullscreenControl"
      :map-id="mapId"
      :map-type-id="mapTypeId"
      :street-view-control="streetViewControl"
      :zoom="zoom"
      class="omni-google-map__map"
    >
      <slot />
    </google-map>

    <v-btn class="omni-google-map__type-toggle" flat height="40" min-width="120" @click="toggleMapType">
      <template #prepend>
        <v-icon size="20">
          {{ mapTypeId === "roadmap" ? "mdi-satellite-variant" : "mdi-map-outline" }}
        </v-icon>
      </template>

      <span class="font-weight-500">
        {{ mapTypeId === "roadmap" ? t("general.satellite-view") : t("general.map-view") }}
      </span>
    </v-btn>
  </div>
</template>

<style lang="scss" scoped>
.omni-google-map {
  position: relative;
  width: 100%;
  height: 100%;

  &__map {
    width: 100%;
    height: 100%;
  }

  &__type-toggle {
    position: absolute;
    bottom: 24px;
    left: 24px;
    z-index: 1;
  }
}
</style>
